import { clsx } from 'clsx'

import { StarIcon } from '@buffer-mono/popcorn'

import styles from './StarRating.module.css'

import type { JSX } from 'react'

type StarRatingProps = {
  /** Rating value from 1-5 */
  rating: number
  /** Size of the stars */
  size?: 'small' | 'medium'
  /** Additional CSS class */
  className?: string
}

/**
 * Displays a star rating visualization (1-5 stars).
 * Used for Google Business Profile review ratings.
 */
export function StarRating({
  rating,
  size = 'small',
  className,
}: StarRatingProps): JSX.Element {
  // Clamp rating to valid range
  const clampedRating = Math.max(0, Math.min(5, Math.round(rating)))

  const iconSize = size === 'small' ? 14 : 16

  return (
    <div
      className={clsx(styles.container, className)}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- The styled star glyphs need an explicit image role and accessible rating name.
      role="img"
      aria-label={`${clampedRating} out of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, index) => {
        const isFilled = index < clampedRating
        return (
          <StarIcon
            key={index}
            size={iconSize}
            className={clsx(styles.star, isFilled && styles.filled)}
            aria-hidden="true"
          />
        )
      })}
    </div>
  )
}
